import { createContext, useContext, useState, ReactNode } from 'react';
import {
  MarketingCompany, MarketingProject, MarketingProduct, ThreeYearStrategy,
  Campaign, ContentItem, SEOKeyword, ResearchRecord, KnowledgebaseArticle,
  IndustryStandard, BlogArticle, Document as MDocument, MarketingTask,
  MarketingNotification, AnalyticsMetric
} from './types';

interface MarketingData {
  companies: MarketingCompany[];
  projects: MarketingProject[];
  products: MarketingProduct[];
  strategies: ThreeYearStrategy[];
  campaigns: Campaign[];
  content: ContentItem[];
  keywords: SEOKeyword[];
  research: ResearchRecord[];
  knowledgebase: KnowledgebaseArticle[];
  standards: IndustryStandard[];
  blogs: BlogArticle[];
  documents: MDocument[];
  tasks: MarketingTask[];
  notifications: MarketingNotification[];
  analytics: AnalyticsMetric[];
  updateCompany: (id: string, data: Partial<MarketingCompany>) => void;
  addCompany: (company: MarketingCompany) => void;
  updateProject: (id: string, data: Partial<MarketingProject>) => void;
  addProject: (project: MarketingProject) => void;
  updateProduct: (id: string, data: Partial<MarketingProduct>) => void;
  addProduct: (product: MarketingProduct) => void;
  updateCampaign: (id: string, data: Partial<Campaign>) => void;
  addCampaign: (campaign: Campaign) => void;
  updateContent: (id: string, data: Partial<ContentItem>) => void;
  addContent: (item: ContentItem) => void;
  addKeyword: (kw: SEOKeyword) => void;
  updateKeyword: (id: string, data: Partial<SEOKeyword>) => void;
  addResearch: (r: ResearchRecord) => void;
  updateResearch: (id: string, data: Partial<ResearchRecord>) => void;
  addKnowledgebase: (k: KnowledgebaseArticle) => void;
  addStandard: (s: IndustryStandard) => void;
  updateStandard: (id: string, data: Partial<IndustryStandard>) => void;
  addBlog: (b: BlogArticle) => void;
  toggleBlogSaved: (id: string) => void;
  addDocument: (d: MDocument) => void;
  updateDocument: (id: string, data: Partial<MDocument>) => void;
  addTask: (t: MarketingTask) => void;
  updateTask: (id: string, data: Partial<MarketingTask>) => void;
  markNotificationRead: (id: string) => void;
}

const MarketingDataContext = createContext<MarketingData | null>(null);

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// Seed Data
const seedCompanies: MarketingCompany[] = [
  {
    id: 'co-1', name: 'Sai Krishna Industries', logo: '⚙️',
    description: 'India\'s leading manufacturer of precision castor wheels and industrial components since 1995.',
    industry: 'Manufacturing', businessModel: 'B2B Industrial Manufacturing',
    targetMarket: 'Textile, Automotive, FMCG, Material Handling', regions: ['North India', 'Pan India', 'International'],
    targetCustomers: 'Factory owners, Procurement managers, Material handling companies',
    objectives: 'Become the #1 castor wheel brand in India by 2028',
    marketingObjectives: 'Build digital presence, generate 500+ leads per month, establish thought leadership',
    brandPositioning: 'Premium quality castor wheels with 28+ years of expertise and 3 manufacturing plants',
    competitors: ['Tente India', 'Grad Castors', 'Elmech Castors', 'TVS Castors', 'Sujan Industries'],
    currentChannels: ['Google Ads', 'IndiaMART', 'Trade Shows', 'Direct Sales', 'LinkedIn'],
    maturity: 'Growing - expanding digital marketing',
    challenges: 'Low digital presence, no brand awareness online, reliance on trade shows',
    opportunities: 'Export markets, e-commerce catalog, content marketing, SEO dominance',
    budget: 1200000, team: 'Marketing Team + External Agency',
    status: 'active', notes: 'Primary company for marketing portal',
    projectIds: ['pr-1', 'pr-2', 'pr-3'], productIds: ['pd-1', 'pd-2', 'pd-3', 'pd-4', 'pd-5'],
    documentIds: [], createdAt: '2026-08-01', updatedAt: '2026-08-29'
  },
  {
    id: 'co-2', name: 'Sai Krishna Plastics Pvt Ltd', logo: '🏭',
    description: 'Specialized plastic and polymer-based industrial components for the castor wheel industry.',
    industry: 'Manufacturing', businessModel: 'B2B Manufacturing',
    targetMarket: 'Industrial, Automotive, Consumer Goods', regions: ['North India', 'Gurgaon NCR'],
    targetCustomers: 'OEMs, Tier-1 suppliers, Industrial equipment manufacturers',
    objectives: 'Expand polymer product line and capture 15% market share in North India',
    marketingObjectives: 'Establish SPPL as a premium polymer components brand',
    brandPositioning: 'Advanced polymer engineering for industrial applications',
    competitors: ['Supreme Industries', 'Paras Chemicals', 'Mohite Industries'],
    currentChannels: ['Trade Shows', 'Direct Sales', 'Referrals'],
    maturity: 'Early stage digital marketing',
    challenges: 'Brand not known outside Gurgaon, no digital presence',
    opportunities: 'E-commerce listing, LinkedIn marketing, industry content',
    budget: 600000, team: 'Marketing Team',
    status: 'active', notes: 'Gurgaon NCR entity',
    projectIds: ['pr-2'], productIds: ['pd-6'],
    documentIds: [], createdAt: '2026-08-01', updatedAt: '2026-08-29'
  },
  {
    id: 'co-3', name: 'Sai Krishna Components Pvt Ltd', logo: '🔩',
    description: 'Precision-engineered bearings, bushes, and metal components for industrial applications.',
    industry: 'Manufacturing', businessModel: 'B2B Manufacturing',
    targetMarket: 'Automotive, Textile, Engineering', regions: ['East India', 'Kolkata'],
    targetCustomers: 'Engineering firms, Automotive suppliers, Textile manufacturers',
    objectives: 'Become East India\'s leading component supplier',
    marketingObjectives: 'Build Kolkata market presence, generate local leads',
    brandPositioning: 'Precision components with ISO quality at competitive prices',
    competitors: ['SKF India', 'NSK Bearings', 'Tata Bearings', 'National Engineering Industries'],
    currentChannels: ['Direct Sales', 'Dealer Network', 'Trade Shows'],
    maturity: 'No digital marketing',
    challenges: 'No online presence, competing with established bearing brands',
    opportunities: 'Local SEO, Google My Business, industrial directories',
    budget: 400000, team: 'Sales Team',
    status: 'active', notes: 'Kolkata entity',
    projectIds: ['pr-3'], productIds: ['pd-7'],
    documentIds: [], createdAt: '2026-08-01', updatedAt: '2026-08-29'
  }
];

const seedProjects: MarketingProject[] = [
  {
    id: 'pr-1', name: 'SKI Digital Transformation 2026', companyId: 'co-1',
    description: 'Complete digital marketing overhaul for Sai Krishna Industries including website, SEO, social media, and lead generation.',
    businessObjective: 'Generate 500+ qualified leads per month, achieve ₹50L revenue through digital channels',
    owner: 'Marketing Director', marketingOwner: 'Digital Marketing Manager',
    targetAudience: 'B2B industrial buyers, procurement managers, factory owners',
    startDate: '2026-09-01', endDate: '2027-03-31', budget: 600000,
    status: 'in_progress', priority: 'high',
    milestones: [
      { id: 'm-1', name: 'Website Launch', dueDate: '2026-09-30', completed: true },
      { id: 'm-2', name: 'SEO Foundation', dueDate: '2026-10-31', completed: false },
      { id: 'm-3', name: 'Social Media Setup', dueDate: '2026-10-15', completed: false },
      { id: 'm-4', name: 'Content Engine Running', dueDate: '2026-11-30', completed: false },
      { id: 'm-5', name: 'Lead Gen Campaigns Live', dueDate: '2026-12-31', completed: false },
    ],
    deliverables: ['Corporate website', 'Product catalog pages', 'Blog with 20 articles', 'Social media profiles', 'Google Business Profile'],
    dependencies: ['Website design approval', 'Product photography', 'Brand guidelines'],
    risks: ['Low team capacity', 'Content quality concerns', 'Budget constraints'],
    channels: ['seo', 'social_media', 'ppc', 'content'],
    productIds: ['pd-1', 'pd-2', 'pd-3'],
    campaignIds: ['cm-1', 'cm-2'], contentIds: ['ct-1', 'ct-2', 'ct-3'],
    documentIds: [], kpis: [
      { id: 'k-1', name: 'Monthly Leads', target: '500', current: '0', unit: 'leads' },
      { id: 'k-2', name: 'Organic Traffic', target: '10000', current: '0', unit: 'visits/month' },
      { id: 'k-3', name: 'Domain Authority', target: '25', current: '5', unit: 'DA' },
    ],
    createdAt: '2026-08-01', updatedAt: '2026-08-29'
  },
  {
    id: 'pr-2', name: 'SPPL Brand Awareness Campaign', companyId: 'co-2',
    description: 'Build brand awareness for SPPL polymer products in Gurgaon NCR region.',
    businessObjective: 'Establish SPPL as known polymer component brand in NCR',
    owner: 'SPPL Marketing Lead', marketingOwner: 'Digital Marketing Manager',
    targetAudience: 'NCR industrial buyers, OEMs, automotive suppliers',
    startDate: '2026-10-01', endDate: '2027-06-30', budget: 300000,
    status: 'planning', priority: 'medium',
    milestones: [
      { id: 'm-6', name: 'Google My Business Setup', dueDate: '2026-10-15', completed: false },
      { id: 'm-7', name: 'LinkedIn Company Page', dueDate: '2026-10-30', completed: false },
      { id: 'm-8', name: 'First Blog Series', dueDate: '2026-11-30', completed: false },
    ],
    deliverables: ['Google My Business listing', 'LinkedIn company page', 'Product brochure PDF', '5 blog articles'],
    dependencies: ['Product photography', 'Brand guidelines finalization'],
    risks: ['Small marketing budget', 'Competing with established brands'],
    channels: ['social_media', 'seo', 'content'],
    productIds: ['pd-6'], campaignIds: [], contentIds: [],
    documentIds: [], kpis: [
      { id: 'k-4', name: 'Brand Search Volume', target: '500', current: '0', unit: 'searches/month' },
      { id: 'k-5', name: 'LinkedIn Followers', target: '1000', current: '0', unit: 'followers' },
    ],
    createdAt: '2026-08-15', updatedAt: '2026-08-29'
  },
  {
    id: 'pr-3', name: 'SCPL Kolkata Market Penetration', companyId: 'co-3',
    description: 'Digital marketing entry for SCPL bearings and components in East India market.',
    businessObjective: 'Generate 50 leads per month from Kolkata industrial belt',
    owner: 'SCPL Sales Head', marketingOwner: 'Regional Marketing Coordinator',
    targetAudience: 'East India industrial buyers, bearing distributors',
    startDate: '2026-11-01', endDate: '2027-06-30', budget: 200000,
    status: 'not_started', priority: 'medium',
    milestones: [
      { id: 'm-9', name: 'Google My Business', dueDate: '2026-11-15', completed: false },
      { id: 'm-10', name: 'IndiaMART Premium', dueDate: '2026-11-30', completed: false },
    ],
    deliverables: ['Google My Business profile', 'IndiaMART listing', 'Local SEO optimization'],
    dependencies: ['Office photos', 'Product catalog in Bengali/English'],
    risks: ['No existing digital presence', 'Limited local competition data'],
    channels: ['seo', 'direct'],
    productIds: ['pd-7'], campaignIds: [], contentIds: [],
    documentIds: [], kpis: [
      { id: 'k-6', name: 'Monthly Inquiries', target: '50', current: '0', unit: 'inquiries' },
    ],
    createdAt: '2026-08-20', updatedAt: '2026-08-29'
  }
];

const seedProducts: MarketingProduct[] = [
  {
    id: 'pd-1', name: 'Swivel Castor Wheels', companyId: 'co-1', projectId: 'pr-1',
    description: 'Premium swivel castor wheels with 360° rotation for material handling',
    category: 'Castor Wheels', customerProblem: 'Heavy loads need smooth, directional mobility',
    solution: 'Precision-engineered swivel castors with ball bearing raceway',
    valueProposition: 'India\'s most reliable swivel castors with 10-12 day delivery',
    usp: '28+ years expertise, 3 manufacturing plants, ISO certified',
    features: ['360° rotation', 'Load capacity 50-2000 kg', 'Multiple wheel materials', 'Custom sizes'],
    benefits: ['Smooth directional control', 'Reduced manual effort', 'Long service life'],
    targetCustomers: 'Textile mills, Automotive plants, Warehouses, FMCG companies',
    buyerPersonas: ['Procurement Manager', 'Factory Manager', 'Warehouse Supervisor'],
    customerJourney: 'Awareness → Product Search → Comparison → Quote Request → Purchase',
    pricing: '₹50 - ₹5,000 per piece depending on specifications',
    positioning: 'Premium quality at competitive pricing',
    competitors: ['Tente', 'Grad', 'Elmech', 'TVS'],
    marketingMessages: ['Engineered for Indian conditions', 'Load tested to international standards'],
    salesMessages: ['ISO certified quality', 'Pan-India delivery in 10-12 days'],
    seoKeywords: ['swivel castor wheels', 'swivel castor manufacturer', 'swivel castor India'],
    launchDate: '1995-01-01', status: 'active',
    owner: 'Product Manager', marketingOwner: 'Digital Marketing Manager',
    campaignIds: ['cm-1'], contentIds: ['ct-1'],
    researchIds: [], documentIds: [], kpis: [
      { id: 'k-7', name: 'Monthly Searches', target: '2400', current: '0', unit: 'searches' },
    ],
    createdAt: '2026-08-01', updatedAt: '2026-08-29'
  },
  {
    id: 'pd-2', name: 'Fixed Castor Wheels', companyId: 'co-1', projectId: 'pr-1',
    description: 'Heavy-duty fixed castor wheels for straight-line movement applications',
    category: 'Castor Wheels', customerProblem: 'Fixed directional load movement without swivel',
    solution: 'Rigid mount fixed castors with superior load bearing',
    valueProposition: 'Rock-solid fixed castors built for demanding industrial use',
    usp: 'Withstands impact loads, maintenance-free operation',
    features: ['Rigid mounting', 'Load capacity 100-3000 kg', 'Various wheel sizes', 'Heavy-duty fork'],
    benefits: ['Stable directional movement', 'Impact resistant', 'Zero maintenance'],
    targetCustomers: 'Automotive assembly lines, Heavy engineering, Logistics',
    buyerPersonas: ['Plant Manager', 'Maintenance Head'],
    customerJourney: 'Awareness → Technical Specs Review → Quote → Order',
    pricing: '₹80 - ₹8,000 per piece',
    positioning: 'Industrial strength fixed castors for heavy loads',
    competitors: ['Tente', 'Grad', 'Blickle'],
    marketingMessages: ['Built for the toughest Indian factory floors'],
    salesMessages: ['Certified load capacity', 'Same-day dispatch for standard sizes'],
    seoKeywords: ['fixed castor wheels', 'rigid castor manufacturer India', 'heavy duty fixed castors'],
    launchDate: '1995-01-01', status: 'active',
    owner: 'Product Manager', marketingOwner: 'Digital Marketing Manager',
    campaignIds: ['cm-1'], contentIds: [],
    researchIds: [], documentIds: [], kpis: [],
    createdAt: '2026-08-01', updatedAt: '2026-08-29'
  },
  {
    id: 'pd-3', name: 'Industrial Wheels', companyId: 'co-1', projectId: 'pr-1',
    description: 'Polyurethane, nylon, and rubber industrial wheels for various applications',
    category: 'Wheels', customerProblem: 'Specialized wheels for specific floor conditions and loads',
    solution: 'Range of wheel materials engineered for different environments',
    valueProposition: 'Right wheel for every floor — from delicate to heavy-duty',
    usp: '6 wheel materials, 22+ sizes, tested for Indian conditions',
    features: ['PP, Nylon, PU, UHMWPE, Cast Iron, Rubber', '80mm-250mm sizes', 'Floor-safe options'],
    benefits: ['Floor protection', 'Chemical resistance', 'Noise reduction'],
    targetCustomers: 'Hospitals, Hotels, Retail, Manufacturing',
    buyerPersonas: ['Facility Manager', 'Operations Head'],
    customerJourney: 'Need Identification → Material Selection → Quote → Purchase',
    pricing: '₹20 - ₹3,000 per wheel',
    positioning: 'Complete wheel range for every application',
    competitors: ['Tente', 'Colson', 'Räder-Vogel'],
    marketingMessages: ['6 materials, 22 sizes, one trusted source'],
    salesMessages: ['Free sample testing available', 'Technical consultation included'],
    seoKeywords: ['industrial wheels India', 'PU wheels manufacturer', 'nylon castor wheels'],
    launchDate: '1998-01-01', status: 'active',
    owner: 'Product Manager', marketingOwner: 'Digital Marketing Manager',
    campaignIds: [], contentIds: [],
    researchIds: [], documentIds: [], kpis: [],
    createdAt: '2026-08-01', updatedAt: '2026-08-29'
  },
  {
    id: 'pd-4', name: 'Lattice Conveyors', companyId: 'co-1', projectId: 'pr-1',
    description: 'Custom lattice conveyor systems for textile and garment industry',
    category: 'Conveyors', customerProblem: 'Textile mills need gentle, continuous fabric movement',
    solution: 'Custom-built lattice conveyors with aluminium/PVC/MS frames',
    valueProposition: 'India\'s lattice conveyor specialist — custom built to your layout',
    usp: 'Made in India, custom dimensions, 5 lag types, belt options',
    features: ['Pentagon/Hexagon/Solid/Semi shapes', 'Aluminium/PVC/MS materials', 'Belt options', 'Custom lengths'],
    benefits: ['Gentle fabric handling', 'Custom fit for any layout', 'Low maintenance'],
    targetCustomers: 'Textile mills, Garment factories, Dyeing units',
    buyerPersonas: ['Plant Manager', 'Production Head', 'Textile Engineer'],
    customerJourney: 'Layout Assessment → Custom Quote → Manufacturing → Installation',
    pricing: '₹15,000 - ₹5,00,000 per meter depending on specifications',
    positioning: 'India\'s leading custom lattice conveyor manufacturer',
    competitors: ['Local fabricators', 'Imported systems'],
    marketingMessages: ['Custom built for your exact specifications', 'Designed for Indian textile conditions'],
    salesMessages: ['Free site survey', 'Installation support included'],
    seoKeywords: ['lattice conveyor manufacturer', 'textile conveyor India', 'lattice conveyor system'],
    launchDate: '2005-01-01', status: 'active',
    owner: 'Product Manager', marketingOwner: 'Digital Marketing Manager',
    campaignIds: [], contentIds: [],
    researchIds: [], documentIds: [], kpis: [],
    createdAt: '2026-08-01', updatedAt: '2026-08-29'
  },
  {
    id: 'pd-5', name: 'Bearings & Components', companyId: 'co-1', projectId: 'pr-1',
    description: 'Ball bearings, bushes, circlips, and precision metal components',
    category: 'Components', customerProblem: 'Need reliable, affordable bearings for castor assembly',
    solution: 'In-house manufactured precision components',
    valueProposition: 'Complete castor components — from bearings to bushes, all under one roof',
    usp: 'In-house manufacturing ensures quality and quick delivery',
    features: ['6201-6206 bearings', 'GB bushes', 'CIR circlips', 'NB nut-bolts'],
    benefits: ['Perfect fit for SKI castors', 'Consistent quality', 'Quick availability'],
    targetCustomers: 'Castor assemblers, Engineering workshops',
    buyerPersonas: ['Assembly Manager', 'Purchase Head'],
    customerJourney: 'Specification Match → Sample Testing → Bulk Order',
    pricing: '₹5 - ₹500 per component',
    positioning: 'One-stop component shop for castor manufacturing',
    competitors: ['SKF', 'NSK', 'Local bearing traders'],
    marketingMessages: ['Engineered to fit, built to last'],
    salesMessages: ['Minimum order just 100 pieces', 'Technical specifications on request'],
    seoKeywords: ['ball bearings manufacturer India', 'castor wheel components', 'industrial bearings'],
    launchDate: '2000-01-01', status: 'active',
    owner: 'Product Manager', marketingOwner: 'Digital Marketing Manager',
    campaignIds: [], contentIds: [],
    researchIds: [], documentIds: [], kpis: [],
    createdAt: '2026-08-01', updatedAt: '2026-08-29'
  },
  {
    id: 'pd-6', name: 'Polymer Components', companyId: 'co-2', projectId: 'pr-2',
    description: 'Injection-molded and extruded polymer components for industrial use',
    category: 'Plastics', customerProblem: 'Need custom plastic components at scale',
    solution: 'Injection molding with precision tooling',
    valueProposition: 'Custom polymer solutions with rapid prototyping',
    usp: 'In-house tooling, 48-hour prototyping, ISO quality',
    features: ['Custom molds', 'Multiple polymer options', 'Batch production', 'Quality testing'],
    benefits: ['Cost-effective at scale', 'Consistent quality', 'Fast turnaround'],
    targetCustomers: 'Automotive OEMs, Consumer goods, Industrial equipment',
    buyerPersonas: ['Product Designer', 'Procurement Manager'],
    customerJourney: 'Design → Prototype → Tooling → Production',
    pricing: '₹2 - ₹500 per piece depending on volume',
    positioning: 'Precision polymer manufacturing partner',
    competitors: ['Supreme Industries', 'Mohite Industries'],
    marketingMessages: ['From prototype to production in 30 days'],
    salesMessages: ['No minimum for prototype orders', 'Free design consultation'],
    seoKeywords: ['injection molding India', 'polymer components manufacturer', 'custom plastic parts'],
    launchDate: '2010-01-01', status: 'active',
    owner: 'Product Manager', marketingOwner: 'Marketing Lead',
    campaignIds: [], contentIds: [],
    researchIds: [], documentIds: [], kpis: [],
    createdAt: '2026-08-01', updatedAt: '2026-08-29'
  },
  {
    id: 'pd-7', name: 'Precision Bearings & Bushes', companyId: 'co-3', projectId: 'pr-3',
    description: 'High-quality ball bearings, needle bearings, and bronze bushes',
    category: 'Components', customerProblem: 'Reliable bearings for textile and automotive applications',
    solution: 'ISO-certified precision bearings at competitive prices',
    valueProposition: 'Import quality at Indian prices',
    usp: 'East India distribution hub, same-day dispatch',
    features: ['Deep groove ball bearings', 'Needle bearings', 'Bronze bushes', 'Sealed options'],
    benefits: ['Extended service life', 'Reduced downtime', 'Competitive pricing'],
    targetCustomers: 'Textile mills, Automotive workshops, Engineering firms',
    buyerPersonas: ['Maintenance Engineer', 'Purchase Manager'],
    customerJourney: 'Specification Match → Sample Order → Bulk Purchase',
    pricing: '₹10 - ₹2,000 per piece',
    positioning: 'Trusted bearing supplier for East India',
    competitors: ['SKF', 'NSK', 'NBC Bearings'],
    marketingMessages: ['ISO certified quality, competitive pricing'],
    salesMessages: ['Same-day dispatch from Kolkata warehouse'],
    seoKeywords: ['ball bearing supplier Kolkata', 'bearing distributor East India'],
    launchDate: '2015-01-01', status: 'active',
    owner: 'Product Manager', marketingOwner: 'Regional Marketing Lead',
    campaignIds: [], contentIds: [],
    researchIds: [], documentIds: [], kpis: [],
    createdAt: '2026-08-15', updatedAt: '2026-08-29'
  }
];

const seedCampaigns: Campaign[] = [
  {
    id: 'cm-1', name: 'Google Ads — Castor Wheels India', companyId: 'co-1', projectId: 'pr-1',
    productName: 'Swivel Castor Wheels', type: 'PPC',
    objective: 'Generate 200 qualified leads per month via Google Search Ads',
    targetAudience: 'B2B industrial buyers searching for castor wheels',
    persona: 'Procurement Manager', funnelStage: 'consideration',
    channels: ['ppc'], budget: 100000,
    startDate: '2026-10-01', endDate: '2027-03-31', owner: 'Digital Marketing Manager',
    team: ['PPC Specialist', 'Content Writer'],
    conversionGoal: 'Quote request form submission',
    expectedResults: '200 leads/month, ₹200 CPL', actualResults: '',
    leads: 0, conversions: 0, revenue: 0, roi: 0,
    status: 'planned', approvalStatus: 'not_submitted',
    notes: 'Targeting 50+ keywords including "castor wheel manufacturer", "swivel castor India"',
    createdAt: '2026-08-15', updatedAt: '2026-08-29'
  },
  {
    id: 'cm-2', name: 'LinkedIn Thought Leadership — SKI', companyId: 'co-1', projectId: 'pr-1',
    productName: 'All Products', type: 'Content Marketing',
    objective: 'Establish SKI as industry thought leader on LinkedIn',
    targetAudience: 'Manufacturing industry professionals, C-suite executives',
    persona: 'Factory Owner / CEO', funnelStage: 'awareness',
    channels: ['social_media', 'content'], budget: 30000,
    startDate: '2026-09-15', endDate: '2027-03-31', owner: 'Content Manager',
    team: ['Content Writer', 'Graphic Designer'],
    conversionGoal: 'Company page follows and engagement',
    expectedResults: '1000 followers, 5% engagement rate', actualResults: '',
    leads: 0, conversions: 0, revenue: 0, roi: 0,
    status: 'planned', approvalStatus: 'not_submitted',
    notes: '3 posts per week: industry insights, product showcases, customer stories',
    createdAt: '2026-08-20', updatedAt: '2026-08-29'
  },
  {
    id: 'cm-3', name: 'SEO Content Blitz — Castor Wheels', companyId: 'co-1', projectId: 'pr-1',
    productName: 'All Products', type: 'SEO',
    objective: 'Rank #1 for 20 high-volume castor wheel keywords within 6 months',
    targetAudience: 'Industrial buyers searching for castor wheel information',
    persona: 'Research Buyer', funnelStage: 'awareness',
    channels: ['seo', 'content'], budget: 50000,
    startDate: '2026-09-01', endDate: '2027-02-28', owner: 'SEO Specialist',
    team: ['Content Writer', 'Technical SEO'],
    conversionGoal: 'Organic traffic and rankings',
    expectedResults: '10,000 organic visits/month by Month 6', actualResults: '',
    leads: 0, conversions: 0, revenue: 0, roi: 0,
    status: 'in_progress', approvalStatus: 'approved',
    notes: 'Publishing 4 articles per month targeting long-tail castor wheel keywords',
    createdAt: '2026-08-01', updatedAt: '2026-08-29'
  }
];

const seedContent: ContentItem[] = [
  {
    id: 'ct-1', title: 'Complete Guide to Choosing the Right Castor Wheel', companyId: 'co-1',
    projectId: 'pr-1', productName: 'Swivel Castor Wheels', type: 'blog_post',
    targetAudience: 'Industrial buyers new to castor selection',
    persona: 'Procurement Manager', funnelStage: 'consideration',
    keyword: 'how to choose castor wheels', searchIntent: 'informational',
    channel: 'seo', owner: 'Content Writer', reviewer: 'SEO Specialist',
    dueDate: '2026-09-15', publishDate: '', status: 'draft',
    cta: 'Request a free consultation', campaignId: 'cm-3',
    brief: 'Comprehensive guide covering load capacity, wheel materials, floor types, mounting options, and brake types.',
    notes: 'Target 2000+ words, include comparison tables and product images',
    metrics: { views: 0, shares: 0, leads: 0 }, createdAt: '2026-08-20'
  },
  {
    id: 'ct-2', title: 'Top 10 Castor Wheel Problems and Solutions in Textile Industry', companyId: 'co-1',
    projectId: 'pr-1', productName: 'All Products', type: 'blog_post',
    targetAudience: 'Textile factory managers and maintenance teams',
    persona: 'Factory Manager', funnelStage: 'awareness',
    keyword: 'castor wheel problems textile', searchIntent: 'informational',
    channel: 'seo', owner: 'Content Writer', reviewer: 'Product Manager',
    dueDate: '2026-09-30', publishDate: '', status: 'briefing',
    cta: 'Download our textile industry case study', campaignId: 'cm-3',
    brief: 'Address common castor wheel issues in textile environments — dust, heat, load, floor damage.',
    notes: 'Include real examples from SKI customer base (anonymized)',
    metrics: { views: 0, shares: 0, leads: 0 }, createdAt: '2026-08-25'
  },
  {
    id: 'ct-3', title: 'Why Indian Manufacturers Choose SKI Castor Wheels — Customer Stories', companyId: 'co-1',
    projectId: 'pr-1', productName: 'All Products', type: 'case_study',
    targetAudience: 'Potential B2B customers evaluating suppliers',
    persona: 'Purchase Head', funnelStage: 'decision',
    keyword: 'castor wheel manufacturer India reviews', searchIntent: 'commercial',
    channel: 'content', owner: 'Marketing Manager', reviewer: 'Director',
    dueDate: '2026-10-15', publishDate: '', status: 'idea',
    cta: 'Get a custom quote', campaignId: 'cm-2',
    brief: 'Feature 3-5 customer testimonials with before/after scenarios.',
    notes: 'Need customer permissions for names and photos',
    metrics: { views: 0, shares: 0, leads: 0 }, createdAt: '2026-08-28'
  },
  {
    id: 'ct-4', title: 'Castor Wheel Load Capacity Calculator — Free Online Tool', companyId: 'co-1',
    projectId: 'pr-1', productName: 'All Products', type: 'landing_page',
    targetAudience: 'Engineers and procurement teams calculating load needs',
    persona: 'Engineer', funnelStage: 'consideration',
    keyword: 'castor wheel load calculator', searchIntent: 'transactional',
    channel: 'seo', owner: 'Web Developer', reviewer: 'SEO Specialist',
    dueDate: '2026-11-01', publishDate: '', status: 'idea',
    cta: 'Download calculator + get quote', campaignId: 'cm-3',
    brief: 'Interactive calculator where users input load, wheels, and floor type to get castor recommendations.',
    notes: 'This is the "money page" — highest commercial intent keyword',
    metrics: { views: 0, shares: 0, leads: 0 }, createdAt: '2026-08-28'
  }
];

const seedKeywords: SEOKeyword[] = [
  { id: 'kw-1', keyword: 'castor wheel manufacturer in India', companyId: 'co-1', productId: 'pd-1', targetPage: '/products/swivel-castors', intent: 'commercial', volume: 2400, competition: 'high', difficulty: 65, cpc: 15, funnelStage: 'consideration', currentRank: 0, targetRank: 1, priority: 'high', owner: 'SEO Specialist', status: 'in_progress', lastReviewed: '2026-08-29' },
  { id: 'kw-2', keyword: 'swivel castor wheels price', companyId: 'co-1', productId: 'pd-1', targetPage: '/products/swivel-castors', intent: 'transactional', volume: 1600, competition: 'medium', difficulty: 45, cpc: 20, funnelStage: 'decision', currentRank: 0, targetRank: 3, priority: 'high', owner: 'SEO Specialist', status: 'in_progress', lastReviewed: '2026-08-29' },
  { id: 'kw-3', keyword: 'industrial castor wheels', companyId: 'co-1', productId: 'pd-2', targetPage: '/products/fixed-castors', intent: 'commercial', volume: 1900, competition: 'high', difficulty: 60, cpc: 12, funnelStage: 'consideration', currentRank: 0, targetRank: 5, priority: 'high', owner: 'SEO Specialist', status: 'not_started', lastReviewed: '2026-08-29' },
  { id: 'kw-4', keyword: 'lattice conveyor system', companyId: 'co-1', productId: 'pd-4', targetPage: '/products/lattice-conveyors', intent: 'commercial', volume: 880, competition: 'low', difficulty: 25, cpc: 8, funnelStage: 'consideration', currentRank: 0, targetRank: 1, priority: 'medium', owner: 'SEO Specialist', status: 'not_started', lastReviewed: '2026-08-29' },
  { id: 'kw-5', keyword: 'ball bearing manufacturer India', companyId: 'co-1', productId: 'pd-5', targetPage: '/products/bearings', intent: 'commercial', volume: 3200, competition: 'high', difficulty: 70, cpc: 18, funnelStage: 'consideration', currentRank: 0, targetRank: 10, priority: 'medium', owner: 'SEO Specialist', status: 'not_started', lastReviewed: '2026-08-29' },
  { id: 'kw-6', keyword: 'heavy duty castor wheels', companyId: 'co-1', productId: 'pd-2', targetPage: '/products/fixed-castors', intent: 'commercial', volume: 1200, competition: 'medium', difficulty: 40, cpc: 14, funnelStage: 'consideration', currentRank: 0, targetRank: 3, priority: 'high', owner: 'SEO Specialist', status: 'in_progress', lastReviewed: '2026-08-29' },
  { id: 'kw-7', keyword: 'polyurethane wheels manufacturer', companyId: 'co-1', productId: 'pd-3', targetPage: '/products/wheels', intent: 'commercial', volume: 720, competition: 'low', difficulty: 20, cpc: 6, funnelStage: 'consideration', currentRank: 0, targetRank: 1, priority: 'medium', owner: 'SEO Specialist', status: 'not_started', lastReviewed: '2026-08-29' },
  { id: 'kw-8', keyword: 'textile conveyor manufacturer India', companyId: 'co-1', productId: 'pd-4', targetPage: '/products/lattice-conveyors', intent: 'commercial', volume: 480, competition: 'low', difficulty: 15, cpc: 5, funnelStage: 'consideration', currentRank: 0, targetRank: 1, priority: 'high', owner: 'SEO Specialist', status: 'not_started', lastReviewed: '2026-08-29' },
];

const seedResearch: ResearchRecord[] = [
  {
    id: 'rs-1', title: 'India Castor Wheel Market Size & Growth 2026', type: 'market',
    companyId: 'co-1', projectId: 'pr-1', productName: 'All Products',
    industry: 'Manufacturing', region: 'India',
    summary: 'India castor wheel market estimated at ₹2,500 Cr growing at 8% CAGR. Organized sector holds 35% share.',
    findings: 'Market fragmented with 500+ small players. Top 10 hold 40% share. E-commerce emerging as new channel. Demand driven by textile, automotive, and warehouse sectors.',
    impact: 'SKI can capture 2% additional market share through digital marketing in 12 months',
    recommendations: 'Focus on SEO for "castor wheel manufacturer India" keywords. Build IndiaMART presence. Target textile industry specifically.',
    sourceTitle: 'India Industrial Wheels Market Report 2026', sourceUrl: '', publishDate: '2026-06-01',
    researchDate: '2026-08-15', reliability: 7, tags: ['market-size', 'growth', 'castor-wheels'],
    owner: 'Marketing Manager', status: 'completed', verified: false, createdAt: '2026-08-15'
  },
  {
    id: 'rs-2', title: 'Competitor Digital Presence Analysis — Top 5 Castor Brands', type: 'competitor',
    companyId: 'co-1', projectId: 'pr-1', productName: 'All Products',
    industry: 'Manufacturing', region: 'India',
    summary: 'Tente India leads digital with DA 35, 50+ blog posts. Grad and Elmech have basic websites. TVS has no dedicated digital strategy.',
    findings: 'Most competitors have weak SEO. Average DA is 15-25. Very few produce regular content. LinkedIn presence minimal across all.',
    impact: 'First-mover advantage in digital marketing for castor wheel industry',
    recommendations: 'Publish 4 articles/month. Target 50 long-tail keywords. Build LinkedIn presence. Create product comparison content.',
    sourceTitle: 'Internal competitor audit', sourceUrl: '', publishDate: '2026-08-20',
    researchDate: '2026-08-20', reliability: 9, tags: ['competitor-analysis', 'SEO', 'digital-presence'],
    owner: 'SEO Specialist', status: 'completed', verified: true, createdAt: '2026-08-20'
  }
];

const seedKnowledgebase: KnowledgebaseArticle[] = [
  {
    id: 'kb-1', title: 'Material Handling Industry — India Overview', industry: 'Manufacturing',
    overview: 'India material handling equipment market worth ₹30,000 Cr. Growing at 12% CAGR driven by warehouse automation and e-commerce.',
    segments: 'Conveyors, Forklifts, Pallet Trucks, Castors, Cranes, Hoists',
    marketSize: '₹30,000 Cr (2026)', growth: '12% CAGR',
    terminology: 'Material Handling, Ergonomics, Load Capacity, Duty Cycle, Floor Load Rating',
    channels: 'Trade Shows (CII, ACMA), IndiaMART, Direct Sales, Distributors',
    trends: 'Automation, IoT-enabled tracking, Lightweight materials, E-commerce warehouse expansion',
    regulations: 'IS standards for material handling equipment, BIS certification, Factory Act compliance',
    risks: 'Import competition, Raw material price volatility, Organized vs unorganized sector',
    opportunities: 'E-commerce warehouse boom, Smart factory adoption, Export potential',
    benchmarks: 'Average marketing spend: 2-4% of revenue. Digital adoption: 15% of industry.',
    painPoints: 'Finding reliable suppliers, Quality consistency, Delivery time, After-sales support',
    recommendedTools: 'IndiaMART, Google Ads, LinkedIn, Trade Shows, CRM (Zoho)',
    tags: ['material-handling', 'manufacturing', 'india', 'castor-wheels', 'conveyors'],
    region: 'India', owner: 'Marketing Manager', lastReviewed: '2026-08-29',
    favorited: true, createdAt: '2026-08-15'
  },
  {
    id: 'kb-2', title: 'B2B Digital Marketing for Manufacturing — Best Practices', industry: 'Digital Marketing',
    overview: 'B2B manufacturing companies allocate 5-10% of revenue to marketing. Digital channels generate 60% of B2B leads by 2026.',
    segments: 'Content Marketing, SEO, PPC, Email, LinkedIn, Trade Shows, Industry Directories',
    marketSize: 'Global B2B digital marketing: $30B (2026)', growth: '15% CAGR',
    terminology: 'MQL, SQL, CAC, LTV, ROAS, CPL, Buyer Journey, Content Funnel',
    channels: 'Google Ads, LinkedIn, Industry Publications, Trade Shows, IndiaMART, Directories',
    trends: 'AI content creation, Account-based marketing, Video marketing, Interactive tools',
    regulations: 'GDPR (for international), CAN-SPAM, Indian IT Act for digital marketing',
    risks: 'Ad fraud, Rising CPCs, Content saturation, Algorithm changes',
    opportunities: 'LinkedIn organic reach, Long-tail SEO, Interactive calculators, Industry authority',
    benchmarks: 'Average B2B CPL: ₹200-₹500. Email open rate: 20-25%. SEO ROI: 5-10x.',
    painPoints: 'Measuring ROI, Content creation at scale, Lead quality, Sales-marketing alignment',
    recommendedTools: 'Google Analytics, SEMrush, HubSpot, Canva, Mailchimp, Hootsuite',
    tags: ['digital-marketing', 'b2b', 'manufacturing', 'best-practices'],
    region: 'India', owner: 'Digital Marketing Manager', lastReviewed: '2026-08-29',
    favorited: true, createdAt: '2026-08-10'
  }
];

const seedStandards: IndustryStandard[] = [
  {
    id: 'st-1', name: 'IS 15488: Castor Wheels — Specification', organization: 'Bureau of Indian Standards (BIS)',
    version: '2024', industry: 'Manufacturing', region: 'India',
    publishDate: '2024-01-01', lastUpdated: '2024-06-01', lastReviewed: '2026-08-29',
    summary: 'Indian Standard specification for industrial castor wheels covering load testing, dimensional requirements, and material specifications.',
    requirements: 'Load capacity testing per IS standard, dimensional tolerances, material composition, finish requirements, marking and packaging',
    checklist: '☐ Verify load test compliance\n☐ Check dimensional tolerances\n☐ Confirm material specs\n☐ Review marking requirements\n☐ Audit packaging standards',
    complianceStatus: 'compliant', owner: 'Quality Manager',
    sourceUrl: '', documentIds: [], createdAt: '2026-08-15'
  },
  {
    id: 'st-2', name: 'ISO 22878: Earth-moving machinery — Castors', organization: 'ISO',
    version: '2004', industry: 'Manufacturing', region: 'International',
    publishDate: '2004-01-01', lastUpdated: '2004-01-01', lastReviewed: '2026-08-29',
    summary: 'International standard for castors used on earth-moving machinery and industrial vehicles.',
    requirements: 'Load ratings, test methods, dimensional standards, environmental requirements',
    checklist: '☐ Review ISO requirements\n☐ Compare with current products\n☐ Identify gaps\n☐ Plan compliance actions',
    complianceStatus: 'under_review', owner: 'Quality Manager',
    sourceUrl: '', documentIds: [], createdAt: '2026-08-20'
  }
];

const seedBlogs: BlogArticle[] = [
  {
    id: 'bg-1', title: 'How to Choose the Right Castor Wheel for Your Factory', author: 'SKI Editorial',
    publisher: 'Industry Today', url: '', publishDate: '2026-07-15', dateAdded: '2026-08-20',
    industry: 'Manufacturing', topic: 'Product Selection', companyRelevance: 'High — directly relates to SKI product range',
    productRelevance: 'Swivel & Fixed Castors, Industrial Wheels',
    summary: 'Comprehensive guide on castor wheel selection covering load, floor, speed, and environment factors.',
    keyLessons: 'Load per wheel = Total load / (number of wheels × safety factor). Wheel material selection depends on floor type.',
    actions: 'Create similar comprehensive guide for SKI website. Target "how to choose castor wheels" keyword.',
    tags: ['castor-wheels', 'product-selection', 'guide'], reliability: 8, saved: true
  }
];

const seedDocuments: MDocument[] = [
  {
    id: 'dc-1', title: 'SKI Digital Marketing Strategy 2026-2027', type: 'strategy',
    companyId: 'co-1', projectId: 'pr-1', productName: 'All Products',
    owner: 'Marketing Director', contributors: ['Digital Marketing Manager', 'SEO Specialist', 'Content Writer'],
    status: 'approved', version: '1.0', createdDate: '2026-08-01', updatedDate: '2026-08-29',
    reviewDate: '2026-09-01', approvalStatus: 'approved',
    tags: ['strategy', 'digital-marketing', '2026'], relatedIds: ['pr-1'],
    comments: [], createdAt: '2026-08-01'
  },
  {
    id: 'dc-2', title: 'Brand Guidelines — SKI Industries', type: 'brand_guidelines',
    companyId: 'co-1', projectId: '', productName: '',
    owner: 'Marketing Director', contributors: ['Design Agency'],
    status: 'draft', version: '0.5', createdDate: '2026-08-15', updatedDate: '2026-08-29',
    reviewDate: '2026-09-15', approvalStatus: 'in_review',
    tags: ['brand', 'guidelines', 'design'], relatedIds: [],
    comments: [], createdAt: '2026-08-15'
  }
];

const seedTasks: MarketingTask[] = [
  {
    id: 'ts-1', title: 'Set up Google Business Profile for SKI Industries', description: 'Create and optimize Google Business Profile with accurate NAP, categories, photos, and posts.',
    companyId: 'co-1', projectId: 'pr-1', productName: '', campaignId: '', contentId: '',
    assignedTo: 'Digital Marketing Manager', priority: 'high',
    startDate: '2026-09-01', dueDate: '2026-09-15', status: 'not_started',
    approvalStatus: 'not_submitted', checklist: [
      { id: 'cl-1', text: 'Create Google Business account', completed: false },
      { id: 'cl-2', text: 'Add business information (NAP, hours, categories)', completed: false },
      { id: 'cl-3', text: 'Upload 20+ high-quality photos', completed: false },
      { id: 'cl-4', text: 'Write business description with keywords', completed: false },
      { id: 'cl-5', text: 'Enable messaging and reviews', completed: false },
    ],
    comments: [], documentIds: [], createdAt: '2026-08-29', updatedAt: '2026-08-29'
  },
  {
    id: 'ts-2', title: 'Publish first 4 SEO blog articles', description: 'Write, optimize, and publish the first batch of 4 SEO-targeted blog articles for SKI website.',
    companyId: 'co-1', projectId: 'pr-1', productName: '', campaignId: 'cm-3', contentId: 'ct-1',
    assignedTo: 'Content Writer', priority: 'high',
    startDate: '2026-09-01', dueDate: '2026-09-30', status: 'in_progress',
    approvalStatus: 'submitted', checklist: [
      { id: 'cl-6', text: 'Article 1: Complete Guide to Castor Wheel Selection', completed: false },
      { id: 'cl-7', text: 'Article 2: Top 10 Textile Industry Castor Problems', completed: false },
      { id: 'cl-8', text: 'Article 3: Customer Success Stories', completed: false },
      { id: 'cl-9', text: 'Article 4: Castor Wheel Load Calculator Landing Page', completed: false },
    ],
    comments: [], documentIds: [], createdAt: '2026-08-29', updatedAt: '2026-08-29'
  },
  {
    id: 'ts-3', title: 'LinkedIn company page setup and content calendar', description: 'Create LinkedIn company page, design banner, write About section, plan first month of posts.',
    companyId: 'co-1', projectId: 'pr-1', productName: '', campaignId: 'cm-2', contentId: '',
    assignedTo: 'Content Manager', priority: 'medium',
    startDate: '2026-09-15', dueDate: '2026-10-01', status: 'not_started',
    approvalStatus: 'not_submitted', checklist: [
      { id: 'cl-10', text: 'Create LinkedIn company page', completed: false },
      { id: 'cl-11', text: 'Design banner and logo', completed: false },
      { id: 'cl-12', text: 'Write About section with keywords', completed: false },
      { id: 'cl-13', text: 'Plan 30-day content calendar', completed: false },
    ],
    comments: [], documentIds: [], createdAt: '2026-08-29', updatedAt: '2026-08-29'
  }
];

const seedNotifications: MarketingNotification[] = [
  { id: 'nt-1', type: 'deadline', title: 'Website Launch Deadline', message: 'Milestone "Website Launch" due in 30 days', link: 'marketing-projects', read: false, createdAt: '2026-08-29' },
  { id: 'nt-2', type: 'assignment', title: 'New Task Assigned', message: 'You have been assigned "Set up Google Business Profile"', link: 'marketing-tasks', read: false, createdAt: '2026-08-29' },
  { id: 'nt-3', type: 'review', title: 'Document Under Review', message: 'Brand Guidelines v0.5 awaiting your review', link: 'marketing-documents', read: true, createdAt: '2026-08-28' },
];

const seedAnalytics: AnalyticsMetric[] = [
  { id: 'an-1', companyId: 'co-1', projectId: 'pr-1', productName: '', channel: 'seo', date: '2026-08', impressions: 5000, clicks: 150, leads: 5, conversions: 1, revenue: 15000, spend: 0 },
  { id: 'an-2', companyId: 'co-1', projectId: 'pr-1', productName: '', channel: 'ppc', date: '2026-08', impressions: 12000, clicks: 400, leads: 20, conversions: 3, revenue: 45000, spend: 25000 },
  { id: 'an-3', companyId: 'co-1', projectId: 'pr-1', productName: '', channel: 'social_media', date: '2026-08', impressions: 3000, clicks: 80, leads: 2, conversions: 0, revenue: 0, spend: 5000 },
  { id: 'an-4', companyId: 'co-1', projectId: 'pr-1', productName: '', channel: 'content', date: '2026-08', impressions: 2000, clicks: 120, leads: 8, conversions: 2, revenue: 25000, spend: 8000 },
];

export function MarketingDataProvider({ children }: { children: ReactNode }) {
  const [companies, setCompanies] = useState<MarketingCompany[]>(seedCompanies);
  const [projects, setProjects] = useState<MarketingProject[]>(seedProjects);
  const [products, setProducts] = useState<MarketingProduct[]>(seedProducts);
  const [strategies] = useState<ThreeYearStrategy[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>(seedCampaigns);
  const [content, setContent] = useState<ContentItem[]>(seedContent);
  const [keywords, setKeywords] = useState<SEOKeyword[]>(seedKeywords);
  const [research, setResearch] = useState<ResearchRecord[]>(seedResearch);
  const [knowledgebase] = useState<KnowledgebaseArticle[]>(seedKnowledgebase);
  const [standards, setStandards] = useState<IndustryStandard[]>(seedStandards);
  const [blogs, setBlogs] = useState<BlogArticle[]>(seedBlogs);
  const [documents, setDocuments] = useState<MDocument[]>(seedDocuments);
  const [tasks, setTasks] = useState<MarketingTask[]>(seedTasks);
  const [notifications, setNotifications] = useState<MarketingNotification[]>(seedNotifications);
  const [analytics] = useState<AnalyticsMetric[]>(seedAnalytics);

  return (
    <MarketingDataContext.Provider value={{
      companies, projects, products, strategies, campaigns, content, keywords,
      research, knowledgebase, standards, blogs, documents, tasks, notifications, analytics,
      updateCompany: (id, data) => setCompanies(prev => prev.map(c => c.id === id ? { ...c, ...data } : c)),
      addCompany: (company) => setCompanies(prev => [...prev, { ...company, id: company.id || generateId() }]),
      updateProject: (id, data) => setProjects(prev => prev.map(p => p.id === id ? { ...p, ...data } : p)),
      addProject: (project) => setProjects(prev => [...prev, { ...project, id: project.id || generateId() }]),
      updateProduct: (id, data) => setProducts(prev => prev.map(p => p.id === id ? { ...p, ...data } : p)),
      addProduct: (product) => setProducts(prev => [...prev, { ...product, id: product.id || generateId() }]),
      updateCampaign: (id, data) => setCampaigns(prev => prev.map(c => c.id === id ? { ...c, ...data } : c)),
      addCampaign: (campaign) => setCampaigns(prev => [...prev, { ...campaign, id: campaign.id || generateId() }]),
      updateContent: (id, data) => setContent(prev => prev.map(c => c.id === id ? { ...c, ...data } : c)),
      addContent: (item) => setContent(prev => [...prev, { ...item, id: item.id || generateId() }]),
      addKeyword: (kw) => setKeywords(prev => [...prev, { ...kw, id: kw.id || generateId() }]),
      updateKeyword: (id, data) => setKeywords(prev => prev.map(k => k.id === id ? { ...k, ...data } : k)),
      addResearch: (r) => setResearch(prev => [...prev, { ...r, id: r.id || generateId() }]),
      updateResearch: (id, data) => setResearch(prev => prev.map(r => r.id === id ? { ...r, ...data } : r)),
      addKnowledgebase: () => {},
      addStandard: (s) => setStandards(prev => [...prev, { ...s, id: s.id || generateId() }]),
      updateStandard: (id, data) => setStandards(prev => prev.map(s => s.id === id ? { ...s, ...data } : s)),
      addBlog: (b) => setBlogs(prev => [...prev, { ...b, id: b.id || generateId() }]),
      toggleBlogSaved: (id) => setBlogs(prev => prev.map(b => b.id === id ? { ...b, saved: !b.saved } : b)),
      addDocument: (d) => setDocuments(prev => [...prev, { ...d, id: d.id || generateId() }]),
      updateDocument: (id, data) => setDocuments(prev => prev.map(d => d.id === id ? { ...d, ...data } : d)),
      addTask: (t) => setTasks(prev => [...prev, { ...t, id: t.id || generateId() }]),
      updateTask: (id, data) => setTasks(prev => prev.map(t => t.id === id ? { ...t, ...data } : t)),
      markNotificationRead: (id) => setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n)),
    }}>
      {children}
    </MarketingDataContext.Provider>
  );
}

export function useMarketingData() {
  const ctx = useContext(MarketingDataContext);
  if (!ctx) throw new Error('useMarketingData must be used within MarketingDataProvider');
  return ctx;
}
